// client/src/components/influencer/InfluencerPointExchange.js
// 포인트 환전 신청 — A안 리디자인: 게이트 체크리스트(계약·통장) + 예상 입금액 + 진행 중 신청 잠금
// (상태 조회·검증·신청·확인 모달 로직은 기존 그대로)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePoints } from '../../contexts/PointContext';

const API_URL = process.env.REACT_APP_API_URL || '';

function InfluencerPointExchange() {
  const nav = useNavigate();
  const { refreshPoints } = usePoints();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestedPoints, setRequestedPoints] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  // 환전 상태 조회
  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/mybiz/points/exchange/status`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.ok) {
        setStatus(data);
      } else {
        setMessage('상태를 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('fetchStatus error:', error);
      setMessage('상태를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 컴포넌트 마운트 시 포인트도 갱신
  useEffect(() => {
    refreshPoints(); // Header 포인트 갱신
    fetchStatus();   // 환전 상태 조회
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 예상 환전 금액 계산 (90%)
  const estimatedExchange = useMemo(() => {
    const points = Number(requestedPoints) || 0;
    return Math.round(points * 0.9);
  }, [requestedPoints]);

  // 유효성 검사
  const isValid = useMemo(() => {
    const points = Number(requestedPoints) || 0;
    if (points < 5000) return false;
    if (points > (status?.availablePoints || 0)) return false;
    if (points % 1 !== 0) return false;
    return true;
  }, [requestedPoints, status]);

  // 환전 신청
  const handleSubmit = async () => {
    if (!isValid) return;

    setSubmitting(true);
    setMessage('');

    try {
      const res = await fetch(`${API_URL}/api/influencer/mybiz/points/exchange/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          requestedPoints: Number(requestedPoints)
        })
      });

      const data = await res.json();

      if (data.ok) {
        setMessage('✅ 환전 신청이 완료되었습니다.');
        setRequestedPoints('');
        setShowConfirmModal(false);

        // 순서 보장: refreshPoints 완료 후 fetchStatus
        await refreshPoints();
        await fetchStatus();
      } else {
        setMessage(`❌ ${data.message || '환전 신청에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('handleSubmit error:', error);
      setMessage('❌ 환전 신청 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-card" style={{ padding: 40, textAlign: 'center', color: '#6b7686', fontSize: 14, fontWeight: 600 }}>
        불러오는 중...
      </div>
    );
  }

  const gateOk = !!status?.contractCompleted && !!status?.bankRegistered;
  const hasPending = !!status?.hasPendingRequest;
  const canSubmit = gateOk && !hasPending && isValid && !submitting;

  // 신청 버튼 문구 (왜 안 눌리는지 버튼이 직접 말해준다 — v4)
  const submitLabel = hasPending
    ? '진행 중인 환전이 끝나면 신청할 수 있어요'
    : !gateOk
      ? '계약·통장 등록 후 신청할 수 있어요'
      : submitting
        ? '처리 중...'
        : '환전 신청하기';

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">포인트 환전 신청</h2>
        <p className="mb-page-description">보유 포인트를 등록된 통장으로 환전해요.</p>
      </div>

      {/* 진행 중인 환전 신청 */}
      {hasPending && (
        <div className="mb-card" style={{ boxShadow: '0 0 0 2px #0fb37a, var(--mb-shadow-card)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="badge badge--blue">처리 중</span>
            <b style={{ fontSize: 15 }}>환전 신청 1건이 진행되고 있어요</b>
            {status?.pendingRequest?.requestedAt && (
              <span className="mb-info-hint" style={{ marginTop: 0, marginLeft: 'auto' }}>
                {new Date(status.pendingRequest.requestedAt).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })} 신청
              </span>
            )}
          </div>
          {status?.pendingRequest && (
            <dl style={{ marginTop: 10, marginBottom: 0 }}>
              <div className="kv"><dt>신청 포인트</dt><dd>{status.pendingRequest.requestedPoints.toLocaleString()}P</dd></div>
              <div className="kv"><dt>예상 입금액 (90%)</dt><dd style={{ color: '#0a8f62' }}>{status.pendingRequest.exchangeAmount.toLocaleString()}원</dd></div>
            </dl>
          )}
          <div className="note note--blue" style={{ marginTop: 8 }}>
            영업일 기준 2일 안에 입금돼요. 입금이 끝나야 새 환전을 신청할 수 있어요.
          </div>
        </div>
      )}

      <div className="mb-grid-2" style={{ alignItems: 'start' }}>
        {/* 왼쪽: 환전 전 확인 게이트 */}
        <div className="mb-card" style={{ margin: 0 }}>
          <h3 className="mb-section-title">환전 전 확인해주세요</h3>

          <div className={`gate ${status?.contractCompleted ? '' : 'is-todo'}`}>
            <i>{status?.contractCompleted ? '✓' : '!'}</i>
            <div style={{ flex: 1 }}>
              <b>프리랜서 계약 {status?.contractCompleted ? '완료' : '필요'}</b>
              <span>{status?.contractCompleted ? '계약이 체결되어 있어요' : '계약서 메일을 받고 서명해주세요'}</span>
            </div>
            {!status?.contractCompleted && (
              <button type="button" className="mb-btn mb-btn-secondary mb-btn-sm" onClick={() => nav('/influencer/mybiz?sub=contract')}>
                계약하러 가기
              </button>
            )}
          </div>

          <div className={`gate ${status?.bankRegistered ? '' : 'is-todo'}`}>
            <i>{status?.bankRegistered ? '✓' : '!'}</i>
            <div style={{ flex: 1 }}>
              <b>환전 통장 {status?.bankRegistered ? '등록됨' : '등록 필요'}</b>
              <span>{status?.bankRegistered ? '등록된 계좌로 입금돼요' : '본인 명의 계좌를 등록해주세요'}</span>
            </div>
            <button type="button" className="mb-btn mb-btn-secondary mb-btn-sm" onClick={() => nav('/influencer/mybiz?sub=bank')}>
              {status?.bankRegistered ? '변경' : '등록하러 가기'}
            </button>
          </div>

          <div className="checkline">
            <i>✓</i>
            <div>
              <b>지급률 90%</b>
              <span>소득세 3.3% 등 세금·수수료 10%가 공제돼요</span>
            </div>
          </div>
          <div className="checkline">
            <i>✓</i>
            <div>
              <b>최소 5,000P부터</b>
              <span>보유 포인트 이내로 신청할 수 있어요 · 영업일 2일 이내 입금</span>
            </div>
          </div>
          <div className="note note--red">신청 즉시 포인트가 차감돼요. 거절되면 전액 복구돼요.</div>
        </div>

        {/* 오른쪽: 신청 폼 */}
        <div className="mb-card" style={{ margin: 0 }}>
          <div className="mb-form-group">
            <label className="mb-form-label">환전 포인트</label>
            <input
              type="number"
              className="mb-form-control"
              value={requestedPoints}
              onChange={(e) => setRequestedPoints(e.target.value)}
              placeholder="5,000"
              min="5000"
              max={status?.availablePoints || 0}
              step="1000"
              disabled={submitting || hasPending || !gateOk}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            />
            <div className="mb-info-hint">
              5,000P 이상, 보유 {(status?.availablePoints || 0).toLocaleString()}P 이내
              {status?.pendingExchangePoints > 0 && ` · 환전 처리 중 ${status.pendingExchangePoints.toLocaleString()}P 별도`}
            </div>
            {requestedPoints && !isValid && (
              <div className="mb-info-hint" style={{ color: '#e5484d' }}>
                {Number(requestedPoints) < 5000 && '최소 5,000P 이상 입력해주세요.'}
                {Number(requestedPoints) >= 5000 && Number(requestedPoints) > (status?.availablePoints || 0) && '보유 포인트를 초과할 수 없어요.'}
                {Number(requestedPoints) % 1 !== 0 && ' 정수만 입력 가능해요.'}
              </div>
            )}
          </div>

          {/* 예상 입금액 */}
          <div
            style={{
              background: '#e7f7f0', borderRadius: 14, padding: '14px 16px', marginBottom: 14,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0a8f62' }}>예상 입금액 (90%)</span>
            <b style={{ fontSize: 19, color: '#0a8f62', fontVariantNumeric: 'tabular-nums' }}>
              {estimatedExchange.toLocaleString()}원
            </b>
          </div>

          {message && (
            <div className={`note ${message.startsWith('✅') ? 'note--mint' : 'note--red'}`} style={{ marginTop: 0, marginBottom: 14 }}>
              {message}
            </div>
          )}

          <button
            type="button"
            className="mb-btn mb-btn-primary mb-btn-lg mb-btn-full"
            onClick={() => setShowConfirmModal(true)}
            disabled={!canSubmit}
          >
            {submitLabel}
          </button>
        </div>
      </div>

      {/* 확인 모달 */}
      {showConfirmModal && (
        <div className="mbm-overlay">
          <div className="mb-card mbm" style={{ width: 420, maxWidth: '92vw', margin: 0, padding: 28 }}>
            <h3 className="mb-section-title" style={{ marginBottom: 14 }}>환전 신청 확인</h3>
            <dl style={{ margin: 0 }}>
              <div className="kv"><dt>신청 포인트</dt><dd>{Number(requestedPoints).toLocaleString()}P</dd></div>
              <div className="kv"><dt>예상 입금액 (90%)</dt><dd style={{ color: '#0a8f62' }}>{estimatedExchange.toLocaleString()}원</dd></div>
            </dl>
            <div className="note note--red" style={{ marginTop: 12 }}>
              환전 신청 시 즉시 포인트가 차감돼요. 거절되면 전액 복구돼요.
            </div>
            <p className="mb-info-hint" style={{ marginTop: 10 }}>
              관리자 확인 후 등록하신 계좌로 입금됩니다.
            </p>
            <div className="mbm-foot" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="mb-btn mb-btn-secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
              >
                취소
              </button>
              <button
                type="button"
                className="mb-btn mb-btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? '처리 중...' : '환전 신청'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InfluencerPointExchange;
